import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, ChevronRightIcon, Meta, MetaRow, PanelHeader, SegmentedControl, SlideOutPanel, useNavSheet } from '@ofself/shell';

// THE DETAIL SHEET — ported whole from legacy/app.js (2026-08-23, final
// phase). Every .sheet-* / .oc-* / .link-* / .habit-* rule in
// legacy/style.css dresses its content.
//
// THE PANEL IS THE KIT'S (owner call 2026-09-24: "the same component as the
// agent sidebar — it should nicely slide in and out"): a right-side
// <SlideOutPanel>, the one the docked IdentityWall renders through — same
// seat, width range, paper, seam, slide and resize grip; the head is the
// kit's <PanelHeader spread> with the wall's close cell (Close ⇄ chevron).
// The window-level modal it replaced — scrim, cream drawer, its own ×, its
// own slide — is gone with its CSS.
//
// BESIDE THE AGENT, NOT OVER IT. The panel is a flex sibling in the row,
// seated just inside the wall: both may stand at once (the kit's right edge
// reserves their SUM — rightEdge.js), which is the point — the open node is
// the agent's context ("this"), so you can talk about it while it is open.
// On a phone each is a PAGE of the frame (`phonePage`), and a phone shows one
// page at a time: opening one puts the other away.
//
// THE ENGINE STAYS THE DOOR: window.openSheet fetches the item (+ tags),
// stamps _openSheetItem for the agent, and announces missions:sheet; every
// write here goes back through window.putJson/postJson + afterNodeWrite so
// each in-memory copy (mission tree, Now, Schedule) stays honest.

const OL_TYPES = [
  { key: 'goal', label: 'goal', glyph: '◇' },
  { key: 'action', label: 'action', glyph: '→' },
  { key: 'habit', label: 'habit', glyph: '↻' },
];
const STATUSES = [
  { key: 'open', label: 'open' },
  { key: 'in_progress', label: 'in progress' },
  { key: 'reviewing', label: 'reviewing' },
  { key: 'paused', label: 'paused' },
  { key: 'completed', label: 'done' },
  { key: 'not_needed', label: 'not needed' },
];
const HORIZON_OPTIONS = [
  { key: 'ongoing', label: 'ongoing' },
  { key: 'short_term', label: 'short term' },
  { key: 'medium_term', label: 'medium term' },
  { key: 'long_term', label: 'long term' },
];
const PRIORITY_OPTIONS = [
  { key: 'low', label: 'low' },
  { key: 'medium', label: 'medium' },
  { key: 'high', label: 'high' },
];
const OL_CADENCES = [
  { key: 'daily', label: 'daily' },
  { key: 'weekly', label: 'weekly' },
  { key: 'monthly', label: 'monthly' },
];
// The four authorable phrases. Mirrored by AGENT_LINK_VERBS in legacy/app.js,
// which is what the agent's json:relate cards commit through — change both
// together, or the agent and the picker will offer different vocabularies.
const LINK_PHRASES = [
  { key: 'depends_on', verb: 'depends on', invert: false },
  { key: 'blocks', verb: 'blocks', invert: true, stores: 'depends_on' },
  { key: 'relates_to', verb: 'relates to', invert: false },
  { key: 'serves', verb: 'serves', invert: false },
];
function linkPhrase(link) {
  if (link.type === 'relates_to') return 'relates to';
  if (link.type === 'serves') return link.direction === 'out' ? 'serves' : 'is served by';
  return link.direction === 'out' ? 'depends on' : 'blocks';
}
function localYmd(d) {
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}
const canon = (s) => window.canonStatus?.(s) ?? s;

// THE SHEET WEARS THE BUILDER'S LEDGER (2026-09-21). A goal, an action and a
// habit are detailed here; a mission is detailed in the Builder — and the two
// had grown different grammars for the same job. The Builder's is the kit's
// <Meta>/<MetaRow> ledger (label left, one fact per line) with the kit's
// <SegmentedControl> for every one-of-many choice, dressed as a track and a
// thumb (.detail-ledger, missions.css). This sheet now speaks it verbatim: same
// classes, same control, so the two cannot drift by a pixel. The stacked
// label-above Field, the paired Row and the hand-rolled PillRow are gone with
// their CSS.
function Choice({ current, options, onPick }) {
  return (
    <SegmentedControl
      options={options.map((o) => ({ value: o.key, label: o.label }))}
      value={current}
      onChange={onPick}
    />
  );
}

// The zone's own abbreviation — the token people actually use.
function TzTag() {
  let label = '';
  try {
    label = new Intl.DateTimeFormat(undefined, { timeZoneName: 'short' })
      .formatToParts(new Date()).find((p) => p.type === 'timeZoneName')?.value || '';
  } catch {}
  const iana = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
  return <span className="sheet-tz" title={iana}>{label || iana.split('/').pop().replace(/_/g, ' ')}</span>;
}

// ── The parent trail + move picker ───────────────────────────────────────────
function MoveTrail({ item, onMoved }) {
  const [editing, setEditing] = useState(false);
  const [targets, setTargets] = useState(null);
  const [q, setQ] = useState('');
  const [active, setActive] = useState(0);
  const inputRef = useRef(null);

  const open = async () => {
    if (editing) return;
    setEditing(true);
    setQ('');
    setActive(0);
    try {
      const data = await window.fetchJson('/api/move-targets');
      setTargets((data.targets || []).filter((t) => t.id !== item.id));
    } catch (e) {
      alert(`Couldn't load move targets — ${e.message}`);
      setEditing(false);
    }
  };
  useEffect(() => { if (editing && targets) inputRef.current?.focus(); }, [editing, targets]);

  const pick = async (newParentId) => {
    try {
      const r = await fetch(window.maybeScopeUrl(`/api/items/${item.id}/parent`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ parent_id: newParentId }),
      });
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        throw new Error(body.error || r.statusText);
      }
      window.invalidateMissionCache?.();
      setEditing(false);
      onMoved();
      window.refreshCurrentSurface?.();
    } catch (e) {
      alert(`Move failed: ${e.message}`);
      setEditing(false);
    }
  };

  if (!editing) {
    return (
      <div className="parent-trail clickable" style={{ margin: '14px 0 0', cursor: 'pointer' }} onClick={open}>
        in {(item.trail && item.trail.length)
          ? item.trail.map((t, i) => <React.Fragment key={i}>{i > 0 && ' → '}<strong>{t}</strong></React.Fragment>)
          : 'no mission yet'} <span className="trail-edit" title="Move">✎</span>
      </div>
    );
  }
  const visible = (targets || []).filter((t) => !q || t.label.toLowerCase().includes(q.toLowerCase().trim()));
  return (
    <div className="parent-trail editing" style={{ margin: '14px 0 0' }}>
      <div className="sheet-autocomplete">
        <input ref={inputRef} type="text" className="sheet-select" autoComplete="off"
               placeholder="Move to mission or goal… (Esc to cancel)"
               value={q}
               onChange={(e) => { setQ(e.target.value); setActive(0); }}
               onKeyDown={(e) => {
                 if (e.key === 'ArrowDown') { e.preventDefault(); setActive((a) => Math.min(a + 1, visible.length - 1)); }
                 else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
                 else if (e.key === 'Enter') { e.preventDefault(); if (visible[active]) pick(visible[active].id); }
                 else if (e.key === 'Escape') { e.preventDefault(); setEditing(false); }
               }}
               onBlur={() => setTimeout(() => setEditing(false), 150)} />
        {visible.length ? (
          <div className="sheet-autocomplete-menu">
            {visible.slice(0, 60).map((t, i) => (
              <button key={t.id} type="button" className={`ac-opt${i === active ? ' on' : ''}`}
                      onMouseDown={(e) => { e.preventDefault(); pick(t.id); }}>
                {t.label}
              </button>
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
}

// ── Assignees — multi-select over group members ──────────────────────────────
function AssigneePicker({ ids, onChange }) {
  const [chosen, setChosen] = useState(ids || []);
  const [q, setQ] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);
  const me = window.currentUserId?.() || null;
  const nameFor = (id) => window.resolveMemberName?.(id) || id.slice(0, 6);
  const commit = (next) => { setChosen(next); onChange(next.slice()); };
  const matches = (window.activeGroupMembers?.() || [])
    .map((m) => ({ user_id: m.user_id, name: m.username || m.user_id.slice(0, 6) }))
    .filter((m) => !chosen.includes(m.user_id) && (!q.trim() || m.name.toLowerCase().includes(q.trim().toLowerCase())))
    .slice(0, 6);
  return (
    <div className="people-picker">
      <div className="people-chips">
        {chosen.map((id) => (
          <span key={id} className="people-chip">
            <span className="pc-name">{nameFor(id)}{id === me ? ' (you)' : ''}</span>
            <button type="button" className="pc-x" aria-label="Remove"
                    onClick={(e) => { e.stopPropagation(); commit(chosen.filter((c) => c !== id)); }}>×</button>
          </span>
        ))}
      </div>
      <input type="text" className="people-input" autoComplete="off"
             placeholder={chosen.length ? 'add someone…' : 'Unassigned'}
             value={q}
             onChange={(e) => { setQ(e.target.value); setMenuOpen(true); }}
             onFocus={() => setMenuOpen(true)}
             onBlur={() => setTimeout(() => setMenuOpen(false), 140)}
             onKeyDown={(e) => {
               if (e.key === 'Enter' && matches[0]) { e.preventDefault(); commit([...chosen, matches[0].user_id]); setQ(''); }
               if (e.key === 'Escape') { e.preventDefault(); setMenuOpen(false); }
             }} />
      {menuOpen && matches.length ? (
        <div className="people-menu">
          {matches.map((m) => (
            <button key={m.user_id} type="button" className="people-menu-row"
                    onMouseDown={(e) => { e.preventDefault(); commit([...chosen, m.user_id]); setQ(''); }}>
              {m.name}{m.user_id === me ? ' (you)' : ''}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

// ── Tags — worn chips + the type-to-filter catalog menu ──────────────────────
function TagsField({ item, onWrite }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [filter, setFilter] = useState('');
  const [active, setActive] = useState(0);
  const [, force] = useState(0);
  const filterRef = useRef(null);
  useEffect(() => { if (menuOpen) filterRef.current?.focus(); }, [menuOpen]);

  const worn = window.tagsForNode?.(item) || [];
  const wornIds = new Set(worn.map((t) => t.id));
  const all = window.allTags?.() || [];
  const matches = all.filter((t) => !filter.trim() || t.label.toLowerCase().includes(filter.trim().toLowerCase()));

  const save = async (tags) => {
    const refs = tags.map((t) => ({ id: t.id, label: t.label }));
    try {
      await window.putJson(`/api/items/${item.id}`, { value_json: { tags: refs } });
      item.tags = refs;
      window.updateLocalNode?.(item.id, { tags: refs });
      window.announceMissionFromSheet?.();
      setMenuOpen(false);
      force((n) => n + 1);
      onWrite();
    } catch (err) {
      alert(`Couldn't save tags — ${err.message}`);
    }
  };
  const toggle = (t) => {
    const current = window.tagsForNode?.(item) || [];
    save(wornIds.has(t.id) ? current.filter((x) => x.id !== t.id) : [...current, t]);
  };

  return (
    <MetaRow label="tags">
      <div className="sheet-tags" onClick={(e) => { if (!e.target.closest('.sheet-tags-menu, .sheet-tags-add')) setMenuOpen(false); }}>
        <div className="sheet-tags-chips">
          {worn.map((t) => (
            <span key={t.id} className="tag-chip" data-color={t.color || 'sand'}>
              <span className="tag-chip-label">{t.label}</span>
              <button type="button" className="tag-chip-x" title={`Remove ${t.label}`}
                      onClick={(e) => { e.stopPropagation(); save(worn.filter((x) => x.id !== t.id)); }}>×</button>
            </span>
          ))}
          <button type="button" className="sheet-tags-add" title="Add a tag"
                  onClick={(e) => { e.stopPropagation(); setMenuOpen((v) => !v); }}>
            {worn.length ? '+' : '+ tag'}
          </button>
        </div>
        <div className="sheet-tags-menu-host">
          {menuOpen && (
            <div className="sheet-tags-menu">
              <input ref={filterRef} type="text" className="sheet-tags-filter" placeholder="Filter tags…" spellCheck={false}
                     value={filter}
                     onChange={(e) => { setFilter(e.target.value); setActive(0); }}
                     onKeyDown={(e) => {
                       if (e.key === 'ArrowDown') { e.preventDefault(); setActive((a) => Math.min(a + 1, matches.length - 1)); }
                       else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
                       else if (e.key === 'Enter') { e.preventDefault(); if (matches[active]) toggle(matches[active]); }
                       else if (e.key === 'Escape') { e.preventDefault(); setMenuOpen(false); }
                     }} />
              <div className="sheet-tags-list">
                {matches.length ? matches.map((t, i) => (
                  <button key={t.id} type="button"
                          className={`sheet-tags-opt${wornIds.has(t.id) ? ' on' : ''}${i === active ? ' active' : ''}`}
                          onMouseEnter={() => setActive(i)}
                          onClick={(e) => { e.stopPropagation(); toggle(t); }}>
                    <span className="tag-chip" data-color={t.color || 'sand'}><span className="tag-chip-label">{t.label}</span></span>
                    <span className="sheet-tags-check">{wornIds.has(t.id) ? '✓' : ''}</span>
                  </button>
                )) : (
                  <div className="sheet-tags-menu-empty">
                    {all.length ? `No tag matches "${filter.trim()}".` : 'No tags yet.'}
                  </div>
                )}
              </div>
              <button type="button" className="sheet-tags-manage"
                      onClick={(e) => { e.stopPropagation(); window.closeSheet?.(); window.navToManage?.(); }}>
                {all.length ? 'Manage tags →' : 'Create one on Manage →'}
              </button>
            </div>
          )}
        </div>
      </div>
    </MetaRow>
  );
}

// ── Habit consistency — cadence-aware dot strip (legacy renderHabitHistory) ──
// The strip is a block, not a fact: it takes the sheet's width, so it is a
// section in the grammar Signals / Outcomes / Files already use, not a ledger row.
function Consistency({ children }) {
  return (
    <div className="sheet-section">
      <div className="sheet-section-title">Consistency</div>
      <div className="habit-hist-body">{children}</div>
    </div>
  );
}
function HabitHistory({ item }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [epoch, setEpoch] = useState(0);
  useEffect(() => {
    if (!item.cadence) return undefined;
    let live = true;
    window.fetchJson(`/api/habits/${item.id}/history?days=180`)
      .then((d) => live && setData(d))
      .catch((e) => live && setError(e.message));
    return () => { live = false; };
  }, [item.id, item.cadence, epoch]);

  if (!item.cadence) {
    return (
      <Consistency><div className="habit-hist-empty">Pick a cadence above to start tracking consistency.</div></Consistency>
    );
  }
  if (error) return <Consistency><div className="habit-hist-empty">Couldn't load history — {error}</div></Consistency>;
  if (!data) return <Consistency><div className="habit-hist-loading">loading…</div></Consistency>;

  const WD = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
  const at = (iso) => new Date(`${iso}T12:00:00`);
  const pad = (n) => String(n).padStart(2, '0');

  const toggleDay = async (iso, status) => {
    const next = status === 'completed' ? 'open' : 'completed';
    const rec = data.days.find((d) => d.date === iso);
    const prev = rec ? rec.status : undefined;
    if (rec) { rec.status = next; setData({ ...data }); }
    try {
      await window.postJson(`/api/habits/${item.id}/log`, { date: iso, status: next });
      await window.refreshMissionDetail?.();
      setEpoch((n) => n + 1);
    } catch (e) {
      if (rec) { rec.status = prev; setData({ ...data }); }
      alert(`Couldn't update — ${e.message}`);
    }
  };

  let cols, caption;
  if (item.cadence === 'weekly' || item.cadence === 'monthly') {
    const keyOf = item.cadence === 'weekly'
      ? (iso) => { const d = at(iso); d.setDate(d.getDate() - ((d.getDay() + 6) % 7)); return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`; }
      : (iso) => iso.slice(0, 7);
    const labelOf = item.cadence === 'weekly'
      ? (k, short) => { const d = at(k); return short ? `${d.getMonth() + 1}/${d.getDate()}` : `week of ${d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`; }
      : (k, short) => { const [y, m] = k.split('-'); const d = new Date(+y, +m - 1, 1); return short ? d.toLocaleDateString(undefined, { month: 'narrow' }) : d.toLocaleDateString(undefined, { month: 'short', year: 'numeric' }); };
    const limit = item.cadence === 'weekly' ? 12 : 6;
    const noun = item.cadence === 'weekly' ? 'weeks' : 'months';
    const buckets = new Map();
    for (const d of data.days) {
      if (!d.scheduled) continue;
      const k = keyOf(d.date);
      const b = buckets.get(k) || { key: k, scheduled: 0, kept: 0, elapsed: 0, started: false };
      b.scheduled += 1;
      if (d.date < data.today) b.elapsed += 1;
      if (d.date <= data.today) b.started = true;
      if (d.status === 'completed') b.kept += 1;
      buckets.set(k, b);
    }
    const ordered = [...buckets.values()].sort((a, b) => (a.key < b.key ? -1 : 1)).slice(-limit);
    cols = ordered.map((b) => {
      let cls, mark = '';
      if (!b.started) cls = 'hd-future';
      else if (b.kept >= b.scheduled) { cls = 'hd-done'; mark = '✓'; }
      else if (b.kept >= b.elapsed) cls = 'hd-due';
      else if (b.kept > 0) { cls = 'hd-partial'; mark = String(b.kept); }
      else { cls = 'hd-missed'; mark = '✗'; }
      return (
        <div className="hd-col" key={b.key}>
          <button className={`hd ${cls}`} disabled title={`${labelOf(b.key)} · ${b.kept}/${b.scheduled} kept`}>{mark}</button>
          <span className="hd-lab">{labelOf(b.key, true)}</span>
        </div>
      );
    });
    caption = `last ${ordered.length} ${noun}`;
  } else {
    cols = data.days.slice(-7).map((d) => {
      let cls, mark = '';
      if (d.status === 'completed') { cls = 'hd-done'; mark = '✓'; }
      else if (d.date === data.today) cls = 'hd-due';
      else { cls = 'hd-missed'; mark = '✗'; }
      return (
        <div className="hd-col" key={d.date}>
          <button className={`hd ${cls} hd-click`} title={d.date}
                  onClick={(e) => { e.stopPropagation(); toggleDay(d.date, d.status || ''); }}>{mark}</button>
          <span className="hd-lab">{WD[at(d.date).getDay()]}</span>
        </div>
      );
    });
    caption = 'last 7 days';
  }
  return (
    <Consistency>
      <div className="habit-dots">{cols}</div>
      <div className="habit-dots-cap">{caption}</div>
    </Consistency>
  );
}

// ── Signals attached to this node ────────────────────────────────────────────
function SignalsSection({ item }) {
  const [signals, setSignals] = useState(null);
  const [error, setError] = useState(null);
  const [epoch, setEpoch] = useState(0);
  useEffect(() => {
    let live = true;
    window.fetchJson(`/api/signals/attached/${item.id}`)
      .then(({ signals }) => live && setSignals(signals || []))
      .catch((e) => live && setError(e.message));
    return () => { live = false; };
  }, [item.id, epoch]);
  if (error) return <div className="sheet-section signals-section"><div className="links-empty">Couldn't load signals — {error}</div></div>;
  if (!signals || !signals.length) return null; // hidden when empty — the normal case
  return (
    <div className="sheet-section signals-section">
      <div className="sheet-section-title">Signals<span className="count">{signals.length}</span></div>
      <div className="links-list">
        {signals.map((sig) => (
          <div className="link-row" key={sig.rel_id}>
            <span className="link-verb">{sig.relation === 'influenced_by' ? 'influenced by' : 'evidenced by'}</span>
            <span className="link-title">{sig.title}</span>
            {sig.schema_name && <span className="link-schema">{sig.schema_name}</span>}
            <button type="button" className="link-x" title="Unattach"
                    onClick={async () => {
                      try {
                        await window.fetchJson(`/api/signals/attach/${sig.rel_id}`, { method: 'DELETE' });
                        setEpoch((n) => n + 1);
                      } catch (e) { alert(`Couldn't unattach — ${e.message}`); }
                    }}>×</button>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Outcomes — what has to be true (goals + actions) ─────────────────────────
function OutcomesSection({ item }) {
  const [rows, setRows] = useState(() => (item.outcomes || []).map((o) => ({ ...o })));
  const save = async (next) => {
    const clean = next
      .map((r) => ({ label: String(r.label || '').trim(), value: r.value ?? '', achieved: !!r.achieved }))
      .filter((r) => r.label);
    try {
      await window.putJson(`/api/items/${item.id}`, { value_json: { outcomes: clean } });
      item.outcomes = clean;
      window.afterNodeWrite?.(item.id);
    } catch (e) { alert(`Couldn't save outcomes — ${e.message}`); }
  };
  const update = (next, persist = true) => { setRows(next); if (persist) save(next); };
  return (
    <div className="sheet-section outcomes-section">
      <div className="sheet-section-title">Outcomes</div>
      <div className="oc-list">
        {rows.map((r, i) => (
          <div className={`oc-edit-row${r.achieved ? ' met' : ''}`} key={i}>
            <button type="button" className="oc-check" title={r.achieved ? 'Achieved' : 'Not yet'}
                    onClick={() => update(rows.map((x, j) => (j === i ? { ...x, achieved: !x.achieved } : x)))} />
            <input className="oc-label" defaultValue={r.label || ''} placeholder="what has to be true"
                   onChange={(e) => { rows[i].label = e.target.value; }}
                   onBlur={() => update([...rows])} />
            <input className="oc-value" defaultValue={r.value == null ? '' : String(r.value)} placeholder="target"
                   onChange={(e) => { rows[i].value = e.target.value; }}
                   onBlur={() => update([...rows])} />
            <button type="button" className="oc-del" title="Remove"
                    onClick={() => update(rows.filter((_, j) => j !== i))}>×</button>
          </div>
        ))}
        <div className="addline oc-add" onClick={() => update([...rows, { label: '', value: '', achieved: false }], false)}>
          + add an outcome
        </div>
      </div>
    </div>
  );
}

// ── Attachments — files on a goal, action or habit ───────────────────────────
// The bytes go BROWSER -> OBJECT STORAGE directly, never through the Missions
// server: it hands back a short-lived presigned URL and only ever sees
// metadata. Three steps, in this order, because step 3 must not run until the
// bytes are actually in storage — registering first would leave a node
// pointing at nothing if the upload failed.
const humanSize = (n) => {
  if (typeof n !== 'number' || !isFinite(n)) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
};

// Paradigm requires a SHA-256 of every upload. Done here rather than server-side
// because the server never holds the bytes — and it is the browser's own
// WebCrypto, so a large file costs one pass and no dependency.
async function sha256Hex(file) {
  const buf = await file.arrayBuffer();
  const digest = await crypto.subtle.digest('SHA-256', buf);
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('');
}

function AttachmentsSection({ item }) {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);
  const [epoch, setEpoch] = useState(0);
  const [busy, setBusy] = useState(null);      // filename mid-upload
  const inputRef = useRef(null);

  useEffect(() => {
    let live = true;
    window.fetchJson(`/api/items/${item.id}/attachments`)
      .then(({ attachments }) => live && setRows(attachments || []))
      .catch((e) => live && setError(e.message));
    return () => { live = false; };
  }, [item.id, epoch]);

  const upload = async (file) => {
    setError(null);
    setBusy(file.name);
    try {
      // The type the browser guessed is the type we ask to be signed, and the
      // same one we send on the PUT. S3 signs Content-Type into the presigned
      // URL, so any disagreement here is a 403 that looks like a broken upload.
      const contentType = file.type || 'application/octet-stream';
      const sas = await window.postJson(`/api/items/${item.id}/attachments/sas`, {
        filename: file.name, content_type: contentType, size_bytes: file.size,
      });

      const put = await fetch(sas.upload_url, {
        method: 'PUT',
        // Content-Type ONLY. S3 signs it into the presigned URL, so it must
        // match what we asked for — and every extra header is one more thing
        // the CORS preflight has to be allowed to send. The dashboard still
        // sends Azure's x-ms-blob-type here; that is left over from before
        // STORAGE_BACKEND became s3 and means nothing to the bucket we run.
        headers: { 'Content-Type': sas.content_type || contentType },
        body: file,
      });
      if (!put.ok) throw new Error(`storage rejected the upload (${put.status})`);

      const { attachment } = await window.postJson(`/api/items/${item.id}/attachments`, {
        blob_key: sas.blob_key,
        filename: file.name,
        mime_type: contentType,
        size_bytes: file.size,
        checksum_sha256: await sha256Hex(file),
      });
      // Paint the new row without a refetch, then reconcile on the next open.
      setRows((prev) => [attachment, ...(prev || [])]);
    } catch (e) {
      setError(e.message || "couldn't attach that file");
    } finally {
      setBusy(null);
      if (inputRef.current) inputRef.current.value = '';   // same file twice must re-fire change
    }
  };

  const open = async (nodeId) => {
    try {
      const { download_url: url } = await window.fetchJson(`/api/attachments/${nodeId}/url`);
      window.open(url, '_blank', 'noopener');
    } catch (e) {
      setError(`couldn't open — ${e.message}`);
    }
  };

  return (
    <div className="sheet-section links-section">
      <div className="sheet-section-title">Files</div>
      <div className="links-list">
        {error && <div className="links-empty">{error}</div>}
        {busy && <div className="link-row att-row is-busy"><span className="link-title">{busy}</span><span className="att-meta">uploading…</span></div>}
        {(rows || []).map((a) => (
          <div className="link-row att-row" key={a.id || a.node_id}>
            <span className="link-title" onClick={() => open(a.node_id)} title="Open">{a.filename}</span>
            <span className="att-meta">{humanSize(a.size_bytes)}</span>
            <button type="button" className="link-x" title="Remove file"
                    onClick={async () => {
                      try {
                        await window.fetchJson(`/api/attachments/${a.node_id}`, { method: 'DELETE' });
                        setEpoch((n) => n + 1);
                      } catch (e) { setError(`couldn't remove — ${e.message}`); }
                    }}>×</button>
          </div>
        ))}
      </div>
      <label className={`addline att-add${busy ? ' is-busy' : ''}`}>
        + add a file
        <input ref={inputRef} type="file" className="att-input" disabled={!!busy}
               onChange={(e) => { const f = e.target.files && e.target.files[0]; if (f) upload(f); }} />
      </label>
    </div>
  );
}

// ── Links — edges in Paradigm's relationship table ───────────────────────────
function LinksSection({ item }) {
  const [links, setLinks] = useState(null);
  const [error, setError] = useState(null);
  const [epoch, setEpoch] = useState(0);
  const [verb, setVerb] = useState(LINK_PHRASES[0].key);
  const [q, setQ] = useState('');
  const [options, setOptions] = useState([]);
  const [active, setActive] = useState(-1);
  const [menuOpen, setMenuOpen] = useState(false);
  const [adding, setAdding] = useState(false);
  const cacheRef = useRef(new Map());
  const seqRef = useRef(0);
  const timerRef = useRef(null);

  useEffect(() => {
    let live = true;
    window.fetchJson(`/api/items/${item.id}/links`)
      .then(({ links }) => live && setLinks(links || []))
      .catch((e) => live && setError(e.message));
    return () => { live = false; };
  }, [item.id, epoch]);

  // A link the AGENT wrote goes through the same route this panel posts to, but
  // from outside React — legacy/app.js announces it after a json:relate card is
  // confirmed. Without this the row is missing from the panel the user is
  // looking at until they reopen the node, which reads as the card having lied.
  useEffect(() => {
    const bump = () => setEpoch((n) => n + 1);
    document.addEventListener('missions:links', bump);
    return () => document.removeEventListener('missions:links', bump);
  }, []);

  const paint = (targets) => {
    setOptions(targets || []);
    setActive(targets && targets.length ? 0 : -1);
    setMenuOpen(!!(targets && targets.length));
  };
  const search = useCallback(async (query) => {
    const qq = query.trim();
    if (qq.length < 2) { setMenuOpen(false); setOptions([]); return; }
    const cache = cacheRef.current;
    if (cache.has(qq)) { paint(cache.get(qq)); return; }
    // narrow the nearest shorter answered query while the fetch lands
    for (let i = qq.length - 1; i >= 2; i--) {
      const prev = cache.get(qq.slice(0, i));
      if (prev) { paint(prev.filter((t) => (t.title || '').toLowerCase().includes(qq.toLowerCase()))); break; }
    }
    const mine = ++seqRef.current;
    try {
      const { targets } = await window.fetchJson(`/api/link-targets?q=${encodeURIComponent(qq)}&exclude=${encodeURIComponent(item.id)}`);
      cache.set(qq, targets || []);
      if (mine !== seqRef.current) return;
      paint(targets);
    } catch { if (mine === seqRef.current) { setMenuOpen(false); setOptions([]); } }
  }, [item.id]);

  const add = async (target) => {
    const phrase = LINK_PHRASES.find((p) => p.key === verb) || LINK_PHRASES[0];
    const fromId = phrase.invert ? target.id : item.id;
    const toId = phrase.invert ? item.id : target.id;
    const type = phrase.stores || phrase.key;
    setQ(''); setMenuOpen(false); setOptions([]);
    try {
      await window.postJson(`/api/items/${fromId}/links`, { to_node_id: toId, relationship_type: type });
      setEpoch((n) => n + 1);
    } catch (e) {
      alert(e.message || "Couldn't add link");
    }
  };

  return (
    <div className="sheet-section links-section">
      <div className="sheet-section-title">Links</div>
      <div className="links-list">
        {error && <div className="links-empty">Couldn't load links — {error}</div>}
        {!error && (links || []).map((l) => (
          <div className="link-row" key={l.id}>
            <span className="link-verb">{linkPhrase(l)}</span>
            <span className="link-title" onClick={() => window.openSheet?.(l.node.id)}>{l.node.title}</span>
            {l.node.schema_name && <span className="link-schema">{l.node.schema_name}</span>}
            <button type="button" className="link-x" title="Remove link"
                    onClick={async () => {
                      try {
                        await window.fetchJson(`/api/links/${l.id}`, { method: 'DELETE' });
                        setEpoch((n) => n + 1);
                      } catch (e) { alert(`Couldn't remove — ${e.message}`); }
                    }}>×</button>
          </div>
        ))}
      </div>
      {!adding && <div className="addline" onClick={() => setAdding(true)}>+ link to a node</div>}
      {adding && <div className="link-add sheet-autocomplete">
        <select className="link-verb-select sheet-select" value={verb} onChange={(e) => setVerb(e.target.value)}>
          {LINK_PHRASES.map((p) => <option key={p.key} value={p.key}>{p.verb}</option>)}
        </select>
        <input type="text" className="sheet-select link-search" placeholder="search for a node…" autoComplete="off" autoFocus
               value={q}
               onChange={(e) => {
                 setQ(e.target.value);
                 clearTimeout(timerRef.current);
                 timerRef.current = setTimeout(() => search(e.target.value), 110);
               }}
               onKeyDown={(e) => {
                 if (e.key === 'Escape') { e.preventDefault(); setMenuOpen(false); if (!q) setAdding(false); return; }
                 if (!menuOpen || !options.length) { if (e.key === 'Enter') e.preventDefault(); return; }
                 if (e.key === 'ArrowDown') { e.preventDefault(); setActive((a) => (a + 1) % options.length); }
                 else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((a) => (a - 1 + options.length) % options.length); }
                 else if (e.key === 'Enter') { e.preventDefault(); if (active >= 0) add(options[active]); }
               }}
               onBlur={() => setTimeout(() => setMenuOpen(false), 140)} />
        {menuOpen && options.length ? (
          <div className="sheet-autocomplete-menu">
            {options.map((t, i) => (
              <button key={t.id} type="button" className={`ac-opt${i === active ? ' on' : ''}`}
                      onMouseDown={(e) => { e.preventDefault(); add(t); }}
                      onMouseEnter={() => setActive(i)}>
                <span className="ac-title">{t.title}</span>
                {t.schema_name && <span className="link-schema">{t.schema_name}</span>}
              </button>
            ))}
          </div>
        ) : null}
      </div>}
    </div>
  );
}

// ── The sheet body for one item ──────────────────────────────────────────────
function SheetBody({ item, reload }) {
  const typeInfo = OL_TYPES.find((t) => t.key === item.kind) || { glyph: '·', label: item.kind };
  const [, force] = useState(0);
  const rerender = () => force((n) => n + 1);

  const saveValueJson = async (patch) => {
    try {
      await window.putJson(`/api/items/${item.id}`, { value_json: patch });
      Object.assign(item, patch);
      rerender();
      await window.afterNodeWrite?.(item.id, patch);
    } catch (err) {
      alert(`Update failed: ${err.message}`);
    }
  };

  // ── title / description (uncontrolled; save on blur, Esc reverts) ──
  const titleRef = useRef(null);
  const descRef = useRef(null);
  // The statement grows to what it says (the Builder's grow), so it never
  // scrolls inside itself or holds a band open for text that is not there.
  const grow = (el) => { if (el) { el.style.height = 'auto'; el.style.height = `${el.scrollHeight}px`; } };
  useEffect(() => { grow(descRef.current); }, [item.id]);
  const saveTitle = async () => {
    const el = titleRef.current;
    const newTitle = el.value.trim();
    if (!newTitle) { el.value = item.title || ''; return; }
    if (newTitle === (item.title || '')) return;
    try {
      await window.putJson(`/api/items/${item.id}`, { title: newTitle });
      item.title = newTitle;
      await window.afterNodeWrite?.(item.id, { title: newTitle });
    } catch (err) {
      alert(`Update failed: ${err.message}`);
      el.value = item.title || '';
    }
  };
  const saveDesc = async () => {
    const el = descRef.current;
    const newDesc = el.value.trim();
    if (newDesc === (item.description || '')) return;
    try {
      await window.putJson(`/api/items/${item.id}`, { value_json: { description: newDesc } });
      item.description = newDesc;
      await window.afterNodeWrite?.(item.id, { description: newDesc });
    } catch (err) {
      alert(`Update failed: ${err.message}`);
      el.value = item.description || '';
    }
  };

  // ── kind-specific composites ──
  const dueRef = { d: useRef(null), t: useRef(null) };
  const saveDue = async () => {
    const d = dueRef.d.current.value;
    if (d && !dueRef.t.current.value) dueRef.t.current.value = '23:59';
    const due_at = d ? window.withLocalOffset(`${d}T${(dueRef.t.current.value || '23:59')}:00`) : null;
    try {
      await window.putJson(`/api/items/${item.id}`, { value_json: { due_at } });
      item.due_at = due_at;
      item.target_date = due_at ? due_at.slice(0, 10) : null;
      await window.afterNodeWrite?.(item.id, { due_at, target_date: item.target_date });
    } catch (err) { alert(`Update failed: ${err.message}`); }
  };
  const convertToHabit = async () => {
    const found = window.findInMission?.(item.id);
    const parentId = found?.parentId || window.currentMissionId?.();
    if (!parentId) { alert("Couldn't work out where this sits."); return; }
    if (!confirm(`Make "${item.title || 'untitled'}" recurring?\n\nA habit is created in its place and this action moves underneath it. You'll pick the cadence next.`)) return;
    try {
      const ids = window.assigneeIds?.(item) || [];
      const habit = await window.postJson(`/api/items/${parentId}/children`, {
        kind: 'habit',
        title: item.title || 'untitled',
        ...(item.description ? { description: item.description } : {}),
        ...(item.priority ? { priority: item.priority } : {}),
        ...(item.effort ? { effort: item.effort } : {}),
        ...(ids.length ? { assignee_ids: ids } : {}),
      });
      await window.putJson(`/api/items/${item.id}/parent`, { parent_id: habit.id });
      window.closeSheet?.();
      const mid = window.currentMissionId?.();
      if (mid) await window.openMission?.(mid);
      window.openSheet?.(habit.id);
    } catch (e) {
      alert(`Couldn't make this recurring — ${e.message}`);
    }
  };
  const dueField = (
    <MetaRow label="due">
      <div className="sheet-datetime">
        <input ref={dueRef.d} type="date" className="detail-date"
               defaultValue={(item.due_at || '').slice(0, 10) || item.target_date || ''} onChange={saveDue} />
        <input ref={dueRef.t} type="time" className="detail-date detail-date--time"
               defaultValue={(item.due_at || '').slice(11, 16) || ((item.due_at || item.target_date) ? '23:59' : '')} onChange={saveDue} />
        <TzTag />
        {/* No separator before it: the group wraps in the sheet's column, and
            a "·" that ends up last on a line reads as a dropped character. The
            button's own wash parts it from the date. */}
        {item.kind === 'action' && (
          <button type="button" className="convert-btn" title="Turn this into a habit, with this action as the first piece of work under it"
                  onClick={convertToHabit}>make recurring</button>
        )}
      </div>
    </MetaRow>
  );

  const schedRef = { d: useRef(null), s: useRef(null), e: useRef(null) };
  const saveSched = async () => {
    const d = schedRef.d.current.value;
    if (!d) return;
    let s = schedRef.s.current.value;
    if (!s) {
      const now = new Date();
      if (d === localYmd(now)) {
        const mins = now.getHours() * 60 + now.getMinutes();
        const next = Math.ceil((mins + 1) / 30) * 30;
        s = next >= 24 * 60 ? '23:30' : `${String(Math.floor(next / 60)).padStart(2, '0')}:${String(next % 60).padStart(2, '0')}`;
      } else s = '09:00';
      schedRef.s.current.value = s;
    }
    let e = schedRef.e.current.value;
    if (!e) {
      const m = parseInt(s.slice(0, 2), 10) * 60 + parseInt(s.slice(3, 5), 10) + 45;
      e = `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
    }
    try {
      const sAt = window.withLocalOffset(`${d}T${s}:00`);
      const eAt = window.withLocalOffset(`${d}T${e}:00`);
      await window.putJson(`/data/schedule/${item.id}`, { scheduled_at: sAt, scheduled_end_at: eAt });
      item.scheduled_at = sAt; item.scheduled_end_at = eAt;
      schedRef.e.current.value = e;
      window.loadSchedule?.();
    } catch (err) { alert(`Couldn't schedule — ${err.message}`); }
  };
  const scheduledField = (
    <MetaRow label="scheduled">
      <div className="sheet-datetime">
        <input ref={schedRef.d} type="date" className="detail-date"
               defaultValue={(item.scheduled_at || '').slice(0, 10)} onChange={saveSched} />
        <input ref={schedRef.s} type="time" className="detail-date detail-date--time"
               defaultValue={(item.scheduled_at || '').slice(11, 16)} onChange={saveSched} />
        <span className="field-sep">–</span>
        <input ref={schedRef.e} type="time" className="detail-date detail-date--time"
               defaultValue={(item.scheduled_end_at || '').slice(11, 16)} onChange={saveSched} />
        <TzTag />
        {item.scheduled_at && (
          <button type="button" className="sheet-field-clear"
                  onClick={async () => {
                    try {
                      await window.putJson(`/data/schedule/${item.id}`, { scheduled_at: null, scheduled_end_at: null });
                      item.scheduled_at = null; item.scheduled_end_at = null;
                      rerender();
                      window.loadSchedule?.();
                    } catch (err) { alert(`Couldn't clear — ${err.message}`); }
                  }}>remove</button>
        )}
      </div>
    </MetaRow>
  );

  const assigneeField = (window.activeGroupMembers?.() || []).length ? (
    <MetaRow label="assignees">
      <AssigneePicker ids={window.assigneeIds?.(item) || []}
                      onChange={async (ids) => { item.assignee_ids = ids; await saveValueJson({ assignee_ids: ids }); }} />
    </MetaRow>
  ) : null;

  // A GOAL AIMS AT A DATE. The due field is an action's — a moment, with a time
  // and a timezone — and writing a goal's target through it stamped 23:59 and a
  // zone onto what is only ever a day. The builder writes target_date directly;
  // so does this. And a goal has no effort: effort is what an action costs.
  const targetField = (
    <MetaRow label="target">
      <input type="date" className="detail-date"
             defaultValue={item.target_date || (item.due_at || '').slice(0, 10) || ''}
             onChange={async (e) => {
               const target_date = e.target.value || null;
               try {
                 await window.putJson(`/api/items/${item.id}`, { value_json: { target_date } });
                 item.target_date = target_date;
                 await window.afterNodeWrite?.(item.id, { target_date });
               } catch (err) { alert(`Update failed: ${err.message}`); }
             }} />
    </MetaRow>
  );
  const priorityField = (
    <MetaRow label="priority"><Choice current={item.priority} options={PRIORITY_OPTIONS} onPick={(v) => saveValueJson({ priority: v })} /></MetaRow>
  );
  // The ledger is one fact per line; priority and effort used to share a line.
  const effortField = (
    <MetaRow label="effort"><Choice current={item.effort} options={PRIORITY_OPTIONS} onPick={(v) => saveValueJson({ effort: v })} /></MetaRow>
  );
  const statusField = (
    <MetaRow label="status"><Choice current={canon(item.status)} options={STATUSES} onPick={(v) => saveValueJson({ status: v })} /></MetaRow>
  );

  // habit schedule shape
  const sched = item.schedule || {};
  const saveSchedule = (patch) => {
    const next = { ...sched, ...patch };
    for (const k of Object.keys(next)) {
      if (next[k] == null || (Array.isArray(next[k]) && next[k].length === 0)) delete next[k];
    }
    if (Object.keys(next).length && !next.timezone) {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      if (tz) next.timezone = tz;
    }
    saveValueJson({ schedule: Object.keys(next).length ? next : null });
  };

  // children list
  let childrenSec = null;
  if (item.kind === 'goal' || item.kind === 'action') {
    const kids = window.findInMission?.(item.id)?.node?.children || [];
    if (kids.length) {
      childrenSec = (
        <div className="sheet-section">
          <div className="sheet-section-title">Inside this {item.kind} · {kids.length}</div>
          {kids.map((child) => {
            const info = OL_TYPES.find((tt) => tt.key === child.kind) || { glyph: '·' };
            let metaText = '';
            if (child.kind === 'goal') metaText = window.olStatusWord?.(child.status, child) || '';
            else if (child.kind === 'action' && child.target_date) metaText = child.target_date;
            else if (child.kind === 'habit') {
              const parts = [];
              if (child.cadence) parts.push(child.cadence);
              if (child.status && canon(child.status) !== 'in_progress') parts.push(window.formatStatus?.(child.status) || child.status);
              metaText = parts.join(' · ');
            }
            return (
              <div key={child.id} className={`sheet-child-row is-${child.kind}`} onClick={() => window.openSheet?.(child.id)}>
                <span className="glyph">{info.glyph}</span>
                <span className="name">{child.title || 'untitled'}</span>
                <span className="meta">{metaText}</span>
              </div>
            );
          })}
        </div>
      );
    }
  }

  const isMissionGoal = item.kind === 'goal' && item.is_mission;
  const doDelete = async () => {
    const childCount = window.findInMission?.(item.id)?.node?.children?.length || 0;
    let confirmMsg;
    if (isMissionGoal) confirmMsg = `Delete "${item.title || 'untitled'}" and everything inside it (goals, actions, habits)? This cannot be undone.`;
    else {
      confirmMsg = `Delete "${item.title || 'untitled'}"?`;
      if (childCount) confirmMsg += `\n\nIt has ${childCount} child item${childCount === 1 ? '' : 's'}; they may end up orphaned.`;
    }
    if (!confirm(confirmMsg)) return;
    const wasCurrentMission = window.currentMissionId?.() === item.id;
    window.closeSheet?.();
    try {
      if (isMissionGoal) await window.cascadeDeleteMission(item.id);
      else {
        const res = await fetch(window.maybeScopeUrl(`/api/items/${item.id}`), { method: 'DELETE', credentials: 'same-origin' });
        if (!res.ok && res.status !== 204) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body.error || res.statusText);
        }
      }
      if (wasCurrentMission) {
        window.dropDeletedMission?.(item.id);
        window.navToMissions?.();
        await window.loadMissions?.();
        return;
      }
      await window.refreshCurrentSurface?.();
    } catch (err) {
      alert(`Delete failed: ${err.message}`);
    }
  };

  const statementHint = item.kind === 'goal' ? 'what this goal is for'
    : item.kind === 'habit' ? 'what this habit is for'
    : item.kind === 'action' ? 'what this action is for' : 'no description yet';

  return (
    <>
      {/* THE HEAD IS THE BUILDER'S (2026-09-21): the name, then the statement
          as a quiet second line — no TITLE / ABOUT labels over them, the block
          under them does the parting. Same classes as the Builder's head so the
          two detail surfaces are one. The type pill stays: a mission never
          needs to say what it is, but a goal, an action and a habit do. */}
      <div className="detail-head">
        <div className="sheet-type-row">
          <span className="sheet-type-pill" data-type={item.kind}>
            <span className="glyph">{typeInfo.glyph}</span><span>{typeInfo.label}</span>
          </span>
        </div>
        <input ref={titleRef} type="text" className="detail-title" defaultValue={item.title || ''} spellCheck={false}
               placeholder={`name this ${item.kind}`}
               onKeyDown={(e) => {
                 if (e.key === 'Enter') { e.preventDefault(); e.target.blur(); }
                 if (e.key === 'Escape') { e.preventDefault(); e.target.value = item.title || ''; e.target.blur(); }
               }}
               onBlur={saveTitle} />
        <textarea ref={descRef} rows={1} className="detail-statement" placeholder={statementHint}
                  defaultValue={item.description || ''} spellCheck={false}
                  onInput={(e) => grow(e.target)} onBlur={saveDesc} />
      </div>
      {/* A root has no parent, and "under unfiled" in italic was describing
          that absence as a state. A mission shows no trail. */}
      {!isMissionGoal && <MoveTrail item={item} onMoved={reload} />}

      {item.kind === 'goal' && (
        <div className="detail-ledger-block"><Meta className="detail-ledger">
          {statusField}
          {assigneeField}
          <MetaRow label="horizon"><Choice current={window.canonHorizon?.(item.horizon) ?? item.horizon} options={HORIZON_OPTIONS} onPick={(v) => saveValueJson({ horizon: v })} /></MetaRow>
          {priorityField}
          {targetField}
          <TagsField item={item} onWrite={rerender} />
        </Meta></div>
      )}
      {item.kind === 'action' && (
        <div className="detail-ledger-block"><Meta className="detail-ledger">
          {statusField}
          {assigneeField}
          {dueField}
          {scheduledField}
          {priorityField}
          {effortField}
          <TagsField item={item} onWrite={rerender} />
        </Meta></div>
      )}
      {item.kind === 'habit' && (
        <div className="detail-ledger-block"><Meta className="detail-ledger">
          <MetaRow label="cadence"><Choice current={item.cadence} options={OL_CADENCES} onPick={(v) => saveValueJson({ cadence: v })} /></MetaRow>
          {item.cadence === 'weekly' && (
            <MetaRow label="days">
              <div className="days-row">
                {['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'].map((d) => {
                  const selected = new Set(sched.days || []);
                  return (
                    <button key={d} className={`day${selected.has(d) ? ' on' : ''}`} title={d}
                            onClick={(e) => {
                              e.stopPropagation();
                              const next = new Set(sched.days || []);
                              if (next.has(d)) next.delete(d); else next.add(d);
                              saveSchedule({ days: [...next] });
                            }}>
                      {{ mon: 'M', tue: 'T', wed: 'W', thu: 'T', fri: 'F', sat: 'S', sun: 'S' }[d]}
                    </button>
                  );
                })}
              </div>
            </MetaRow>
          )}
          {item.cadence === 'monthly' && (
            <MetaRow label="day of month">
              <input type="number" min={1} max={31} className="detail-date detail-date--time" placeholder="1–31"
                     defaultValue={sched.day_of_month || ''}
                     onChange={(e) => saveSchedule({ day_of_month: e.target.value ? parseInt(e.target.value, 10) : null })} />
            </MetaRow>
          )}
          <MetaRow label="time">
            <div className="sheet-datetime">
              <input type="time" className="detail-date detail-date--time" defaultValue={sched.time || ''}
                     onChange={(e) => {
                       const st = e.target.value || null;
                       let en = sched.end_time || null;
                       if (!st || (en && en <= st)) en = null;
                       saveSchedule({ time: st, end_time: en });
                     }} />
              <span className="field-sep">–</span>
              <input type="time" className="detail-date detail-date--time" defaultValue={sched.end_time || ''}
                     onChange={(e) => {
                       const st = sched.time || null;
                       let en = e.target.value || null;
                       if (!st || (en && en <= st)) en = null;
                       saveSchedule({ time: st, end_time: en });
                     }} />
            </div>
          </MetaRow>
          {statusField}
          {assigneeField}
          {priorityField}
          {effortField}
        </Meta></div>
      )}
      {item.kind === 'habit' && <HabitHistory item={item} />}

      <SignalsSection item={item} />
      {(item.kind === 'goal' || item.kind === 'action') && <OutcomesSection item={item} />}
      <AttachmentsSection item={item} />
      <LinksSection item={item} />
      {childrenSec}

      {item.created_at && (
        <div className="sheet-provenance">
          {window.sheetCreatorName?.(item)
            ? `created by ${window.sheetCreatorName(item)} · ${item.created_at.split('T')[0]}`
            : `created · ${item.created_at.split('T')[0]}`}
        </div>
      )}
      {/* The status buttons that stood here ("Mark complete", "Pause") said what
          the STATUS control in the ledger already says, in a louder voice — the
          one filled button on the sheet. Gone (2026-09-22). */}
      <div className="sheet-delete-row">
        <Button variant="quiet" tone="danger" onClick={doDelete}>
          {isMissionGoal ? 'delete mission' : `delete ${item.kind}`}
        </Button>
      </div>
    </>
  );
}

// The wall's own numbers (IdentityWall.jsx): one seat, one width range.
const PANEL_MIN = 320;
const PANEL_MAX = 560;
const PANEL_INITIAL = 420;
// how long the kit's slide takes (--ofself-panel-slide-dur, 0.6s) — the
// closing panel keeps its content until it has gone
const SLIDE_MS = 650;

export default function Sheet({ agentOpen = false, closeAgent }) {
  const [state, setState] = useState(null); // { item } | { loading } | { error } | null
  // what the panel shows: the last state, held through the closing slide so
  // the content retreats behind the curtain instead of vanishing first
  const [shown, setShown] = useState(null);
  const [epoch, setEpoch] = useState(0);
  const phone = !!useNavSheet()?.isMobile;
  useEffect(() => {
    const on = (e) => {
      setState(e.detail);
      if (e.detail) { setShown(e.detail); setEpoch((n) => n + 1); }
    };
    document.addEventListener('missions:sheet', on);
    return () => document.removeEventListener('missions:sheet', on);
  }, []);
  const open = !!state;
  useEffect(() => {
    if (open) return undefined;
    const t = setTimeout(() => setShown(null), SLIDE_MS);
    return () => clearTimeout(t);
  }, [open]);
  const close = useCallback(() => window.closeSheet?.(), []);

  // ESCAPE CLOSES THE NEARER PANEL. The detail stands inside the wall, so it
  // takes the first press; the wall (its own listener, on window) the next.
  // Typing keeps its own Escape (a title reverts, a menu shuts), and so does
  // any kit menu or modal standing over the page.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key !== 'Escape' || e.metaKey || e.ctrlKey || e.altKey || e.defaultPrevented) return;
      if (document.getElementById('rf-summon')) return;
      const t = e.target;
      if (t && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName))) return;
      if (document.querySelector('[data-radix-popper-content-wrapper], .ofself-modal[data-state="open"]')) return;
      e.stopPropagation();
      close();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, close]);

  // ONE PAGE AT A TIME ON A PHONE: the detail and the wall are both pages
  // there, so whichever opens puts the other away.
  useEffect(() => { if (phone && open) closeAgent?.(); }, [phone, open]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (phone && agentOpen) window.closeSheet?.(); }, [phone, agentOpen]);

  return (
    <SlideOutPanel
      open={open}
      onClose={close}
      side="right"
      min={PANEL_MIN}
      max={PANEL_MAX}
      initial={PANEL_INITIAL}
      storageKey="missions-detail-width"
      className="missions-detail-panel"
      hideClose
      phonePage={{ label: 'Details', backLabel: 'Back' }}
    >
      {/* NOT `spread`: one item in a spread header is centred, and a lone
          chevron floating mid-panel reads as decoration. A close belongs on
          the panel's own edge. The control itself is the kit's swap word —
          the same one every ofSelf panel closes with — so it is not dressed
          here; making missions' the odd one out is not conforming to the app. */}
      <PanelHeader items={[
        { id: 'close', label: 'Close', ariaLabel: 'Close details', glyph: <ChevronRightIcon />, onSelect: close },
      ]} />
      <div className="block__body missions-detail">
        {shown?.loading && <div className="loading">loading…</div>}
        {shown?.error && <div className="empty-state">Couldn't load: {shown.error}</div>}
        {shown?.item && <SheetBody key={`${shown.item.id}-${epoch}`} item={shown.item}
                                   reload={() => window.openSheet?.(shown.item.id)} />}
      </div>
    </SlideOutPanel>
  );
}
